import { h, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { Swiper, SwiperSlide } from 'swiper/react';
import { IconClose } from 'utils/icon';
import Image from 'utils/image';
import Preloader from 'utils/preloader';
import Panel from 'utils/panel';
import Flyout from 'utils/flyout';
import fetchProduct from 'utils/fetch-product';
import useWindowSize from 'utils/useWindowSize';
import {
  formatProductData,
  formatVariantData,
} from 'utils/dataLayerProductData';
import ProductForm from '../product/product-form';
import NotifyMe from '../product/notify-me';

const ShopTheLookModal = ({ heading, chartPopup, yotpoApiKey }) => {
  const { isTablet, isMobile } = useWindowSize();
  const [product, setProduct] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentVariant, setCurrentVariant] = useState(null);
  const [productsList, setProductsList] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isStylitics, setIsStylitics] = useState(false);
  const openPanelEvent = new CustomEvent('look-add:open');
  const closePanelEvent = new CustomEvent('look-add:close');
  const [isLoading, setIsLoading] = useState(false);
  const [listId, setListId] = useState(null);
  const [listName, setListName] = useState(null);
  const [addToCartAttributeName, setAddToCartAttributeName] = useState(null);

  useEffect(() => {
    const handleLookAddOpen = async (event) => {
      setIsLoading(true);
      const {
        stylitics,
        products: productHandles,
        id,
        slotIndex,
        name,
        selectedIndex,
      } = event.detail;
      const currentIndex = selectedIndex || 0;
      const products = [];
      for (let i = 0; i < productHandles.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const { product: fetchedProduct } = await fetchProduct(
          productHandles[i],
        );
        products.push(fetchedProduct);
      }
      const filteredProducts = products.filter((p) => p);
      setProductsList(filteredProducts);
      setActiveIndex(currentIndex);
      setProduct(filteredProducts[currentIndex]);
      setCurrentVariant(filteredProducts[currentIndex].variants[0]);
      setIsVisible(true);
      setAddToCartAttributeName(`get_the_look|slot_${slotIndex}`);
      if (stylitics) {
        setIsStylitics(stylitics);
        setListId(id);
        setListName(name);
        handleViewItemListTracking(filteredProducts, id, name);
      }
      document.dispatchEvent(openPanelEvent);
      setIsLoading(false);
    };

    window.addEventListener('openLookAdd', handleLookAddOpen);
    return () => {
      window.removeEventListener('openLookAdd', handleLookAddOpen);
    };
  }, []);

  useEffect(() => {
    if (isStylitics && currentVariant && product) {
      const items = [formatVariantData(currentVariant, product)];
      window.dataLayer.push({
        event: 'ee.view_item',
        component_name: 'get_the_look_widget',
        ecommerce: {
          currencyCode: Shopify.currency.active,
          value: parseInt(product.priceRange.minVariantPrice.amount, 10),
          item_list_id: listId,
          item_list_name: listName,
          items,
        },
      });
    }
  }, [currentVariant]);

  const handleViewItemListTracking = (products, id, name) => {
    const items = products.map((item, index) => formatProductData(item, index));

    window.dataLayer.push({
      event: 'ee.view_item_list',
      ecommerce: {
        currencyCode: Shopify.currency.active,
        item_list_id: id,
        item_list_name: name,
        items,
      },
    });
  };

  const availableColorsArray = product?.availableColors?.value
    ? JSON.parse(product?.availableColors?.value)
    : [];
  const currentColor = product?.variants[0].selectedOptions
    .find((option) => option.name === 'Color')
    ?.value.toLowerCase();

  if (currentColor && availableColorsArray.length > 0) {
    // Put the currentColor object entry first in the array
    const currentColorIndex = availableColorsArray.findIndex(
      (color) => Object.keys(color)[0] === currentColor,
    );
    availableColorsArray.unshift(
      availableColorsArray.splice(currentColorIndex, 1)[0],
    );
  }

  const handleItemClick = (p, i) => {
    setProduct(p);
    setActiveIndex(i);
    if (!isStylitics) return;

    const styliticsItemClickEvent = new CustomEvent('stylitics:item-click', {
      detail: {
        product: p,
      },
    });
    window.dispatchEvent(styliticsItemClickEvent);

    const items = [formatProductData(p, i)];
    window.dataLayer.push({
      event: 'ee.select_item',
      ecommerce: {
        currencyCode: Shopify.currency.active,
        item_list_id: listId,
        item_list_name: listName,
        items,
      },
    });
  };

  return isMobile || isTablet ? (
    <>
      {isLoading && <Preloader />}
      <Panel
        context="look-add"
        alignment="bottom"
        overlay={true}
        bodyScrollLock={true}
        zIndex={'z-[48]'}
        classes={'max-h-none h-full max-w-none'}
      >
        {productsList.length && (
          <div>
            <div className="flex justify-between pt-3 pb-2 px-5 bg-core-2">
              <h5 className="body-3 text-text-default">{heading}</h5>
              <button onClick={() => document.dispatchEvent(closePanelEvent)}>
                <IconClose />
              </button>
            </div>
            <ul className="w-full overflow-x-auto px-5 py-4 gap-3.5 flex scrollbar-hide border-y border-gray-400">
              {productsList.map((prod, index) => (
                <li
                  key={index}
                  className={`min-w-[46px] w-[46px] h-[69px] cursor-pointer ${
                    index === activeIndex ? 'border border-gray-900' : ''
                  }`}
                  onClick={() => handleItemClick(prod, index)}
                >
                  <Image
                    classes="object-cover w-full h-full"
                    {...((prod.images?.edges && prod.images.edges[0]?.node) ||
                      prod.images[0])}
                  />
                </li>
              ))}
            </ul>
            <div className="h-[calc(100dvh-143px)] pb-24 overflow-y-auto scrollbar-hide">
              {isVisible && (
                <Swiper
                  className="mt-4 mb-3.5 !px-5"
                  allowTouchMove={true}
                  spaceBetween={4}
                  slidesPerView={2.6}
                >
                  {(product.images?.edges || product.images)?.map(
                    (img, index) => (
                      <SwiperSlide
                        key={`${product.id}-${index}`}
                        className="aspect-[140/211]"
                      >
                        <Image
                          lazyload={false}
                          srcWidths={{ sm: 640 }}
                          classes="object-cover h-full"
                          {...(img?.node || img)}
                        />
                      </SwiperSlide>
                    ),
                  )}
                </Swiper>
              )}
              <div className="px-5">
                <ProductForm
                  product={product}
                  activeProduct={product}
                  setCurrentVariant={setCurrentVariant}
                  currentVariant={currentVariant}
                  hideBreadCrumbs={false}
                  availableColorsArray={availableColorsArray || []}
                  setActiveProduct={setProduct}
                  chartPopup={chartPopup}
                  sizeSelected={!!currentVariant}
                  showDetailsButton={true}
                  isNotPdp={true}
                  colorLimit={100}
                  atcSticky={true}
                  isStylitics={isStylitics}
                  listId={listId}
                  listName={listName}
                  addToCartAttributeName={addToCartAttributeName}
                  settings={{
                    yotpoApiKey,
                  }}
                />
                {(currentVariant && !currentVariant.availableForSale) ||
                Boolean(product.hideAddToCart) ? (
                  <NotifyMe
                    activeVariant={currentVariant}
                    classes="bg-gray-0 w-full"
                    product={product}
                  />
                ) : null}
              </div>
            </div>
          </div>
        )}
      </Panel>
    </>
  ) : (
    <>
      {isLoading && <Preloader />}
      <Flyout
        context="look-add"
        overlay={true}
        bodyScrollLock={true}
        positionClasses="top-1/2 overflow-visible left-1/2 -translate-y-1/2 -translate-x-1/2 lg:max-w-[calc(100%-100px)] lg:!w-[990px] lg:mx-30px"
      >
        <button
          onClick={() => document.dispatchEvent(closePanelEvent)}
          className="absolute left-full top-0 ml-4 w-[30px] h-[30px] rounded-full bg-white flex items-center justify-center"
        >
          <IconClose className="w-3.5 h-3.5" />
        </button>
        {productsList.length && (
          <div className="flex bg-white h-full">
            <div className="border-r border-gray-300">
              <h5 className="body-3 text-text-default bg-core-2 px-6 py-2.5">
                {heading}
              </h5>
              <ul className="h-[calc(100%-40px)] overflow-y-auto px-4 scrollbar-hide py-3.5">
                {productsList.map((prod, index) => (
                  <li
                    key={index}
                    className={`mb-1.5 w-[100px] h-[149px] cursor-pointer ${
                      index === activeIndex ? 'border border-gray-900' : ''
                    }`}
                    onClick={() => handleItemClick(prod, index)}
                  >
                    <Image
                      classes="object-cover w-full h-full"
                      {...((prod.images?.edges && prod.images.edges[0]?.node) ||
                        prod.images[0])}
                    />
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 grid-rows-none gap-1 h-full overflow-y-auto px-3 lg:scrollbar-track:my-3.5 lg:scrollbar:w-0.5 lg:scrollbar:rounded-[20px] lg:scrollbar-thumb:rounded-[20px] lg:scrollbar:my-2.5 lg:scrollbar-track:bg-gray-400 lg:scrollbar-thumb:bg-gray-700">
              {(product?.images?.edges || product?.images)?.map(
                (img, index) => (
                  <Image lazyload={false} key={index} {...(img?.node || img)} />
                ),
              )}
            </div>
            <div className="px-8 min-w-[390px] h-full overflow-y-auto scrollbar-hide pb-8 ">
              <ProductForm
                product={product}
                activeProduct={product}
                setCurrentVariant={setCurrentVariant}
                currentVariant={currentVariant}
                hideBreadCrumbs={false}
                availableColorsArray={availableColorsArray || []}
                setActiveProduct={setProduct}
                sizeSelected={!!currentVariant}
                showDetailsButton={true}
                chartPopup={chartPopup}
                isNotPdp={true}
                isStylitics={isStylitics}
                addToCartAttributeName={addToCartAttributeName}
                listId={listId}
                listName={listName}
                colorLimit={100}
                settings={{
                  yotpoApiKey,
                }}
              />
              {(currentVariant && !currentVariant.availableForSale) ||
              Boolean(product.hideAddToCart) ? (
                <NotifyMe
                  activeVariant={currentVariant}
                  classes="bg-gray-0 w-full"
                  product={product}
                />
              ) : null}
            </div>
          </div>
        )}
      </Flyout>
    </>
  );
};

export default ShopTheLookModal;
